import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllPosts } from '../../data/blogIndex';
import SEOHead from '../SEO/SEOHead';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const posts = getAllPosts();
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-dark-slate mb-4">Post Not Found</h1>
          <p className="text-muted-grey mb-8">The article you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-dark-slate text-white rounded-full font-medium hover:bg-deep-charcoal transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const currentIndex = posts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? posts[currentIndex - 1] : null;
  const nextPost = currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null;

  const paragraphs = post.content.split('\n\n').filter((p) => p.trim());

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          date: post.date,
          category: post.category,
        }}
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-pink-50 via-white to-cream pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center text-muted-grey hover:text-dark-slate transition-colors mb-8"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to all articles
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 bg-white/60 backdrop-blur-sm text-muted-grey text-sm font-medium rounded-full border border-gray-100">
                {post.category}
              </span>
              <span className="text-sm text-muted-grey">
                {formatDate(post.date)}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark-slate tracking-tight leading-tight mb-6">
              {post.title}
            </h1>

            <div className="flex items-center gap-4">
              <div>
                <p className="font-semibold text-dark-slate">North</p>
                <p className="text-sm text-muted-grey">VP Engineering & Founder, North</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Article Content */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="max-w-3xl mx-auto px-6 py-16"
      >
        <div className="prose prose-lg max-w-none">
          {paragraphs.map((paragraph, index) => {
            if (paragraph.startsWith('## ')) {
              return (
                <h2
                  key={index}
                  className="text-3xl font-bold text-dark-slate mt-12 mb-6 tracking-tight"
                >
                  {paragraph.replace('## ', '')}
                </h2>
              );
            }
            if (paragraph.startsWith('### ')) {
              return (
                <h3
                  key={index}
                  className="text-2xl font-bold text-dark-slate mt-10 mb-4 tracking-tight"
                >
                  {paragraph.replace('### ', '')}
                </h3>
              );
            }
            return (
              <p
                key={index}
                className="text-lg text-muted-grey leading-relaxed mb-6"
              >
                {paragraph}
              </p>
            );
          })}
        </div>
      </motion.article>

      {/* Post Navigation */}
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="border-t border-gray-100 pt-12">
          <div className="grid md:grid-cols-2 gap-8">
            {prevPost && (
              <Link
                to={`/blog/${prevPost.slug}`}
                className="group"
              >
                <span className="text-sm text-muted-grey mb-2 block">Previous Article</span>
                <span className="font-semibold text-dark-slate group-hover:text-dark-navy transition-colors">
                  {prevPost.title}
                </span>
              </Link>
            )}
            {nextPost && (
              <Link
                to={`/blog/${nextPost.slug}`}
                className="group md:text-right"
              >
                <span className="text-sm text-muted-grey mb-2 block">Next Article</span>
                <span className="font-semibold text-dark-slate group-hover:text-dark-navy transition-colors">
                  {nextPost.title}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
